import { useCallback, useEffect, useRef, useState } from "react";
import { SettingsTypes } from "../../../types";

const setInitialState = (settings: SettingsTypes) => {
  const {
    rowHeight,
    visibleRowCount,
    bufferRowCount,
    minRowIndex,
    maxRowIndex,
    initialRowIndex,
  } = settings;

  const viewportHeight = visibleRowCount * rowHeight;
  const totalHeight = (maxRowIndex - minRowIndex + 1) * rowHeight;
  const toleranceHeight = bufferRowCount * rowHeight;
  const bufferHeight = viewportHeight + 2 * toleranceHeight;
  const bufferedItems = visibleRowCount + 2 * bufferRowCount;
  const itemsAbove = initialRowIndex - bufferRowCount - minRowIndex;
  const topPaddingHeight = itemsAbove * rowHeight;
  const bottomPaddingHeight = totalHeight - topPaddingHeight;
  const initialPosition = topPaddingHeight + toleranceHeight;

  return {
    viewportHeight,
    totalHeight,
    toleranceHeight,
    bufferHeight,
    bufferedItems,
    topPaddingHeight,
    bottomPaddingHeight,
    initialPosition,
    data: [] as any[],
    minIndex: minRowIndex,
    itemHeight: rowHeight,
  };
};

export const useScroller = (
  settings: SettingsTypes,
  get: (index: number, bufferedItems: number) => any[],
  scrollTop: number
) => {
  const [config, setConfig] = useState(setInitialState(settings));

  const {
    itemHeight,
    minIndex,
    totalHeight,
    toleranceHeight,
    bufferedItems,
    initialPosition,
  } = config;

  const topPaddingHeight = useRef(initialPosition - toleranceHeight);
  const bottomPaddingHeight = useRef(totalHeight - initialPosition);
  const [data, setData] = useState<any[]>([]);

  const lastIndex = useRef<number | null>(null);

  useEffect(() => {
    setConfig(setInitialState(settings));
  }, [settings]);

  useEffect(() => {
    const index = Math.floor(scrollTop / itemHeight);
    const newData = get(index, bufferedItems);

    const newTopPaddingHeight = Math.max(index * itemHeight, 0);
    const newBottomPaddingHeight = Math.max(
      totalHeight - newTopPaddingHeight - newData.length * itemHeight,
      0
    );

    topPaddingHeight.current = newTopPaddingHeight;
    bottomPaddingHeight.current = newBottomPaddingHeight;
    setData(newData);
  }, [scrollTop, settings, get, itemHeight, bufferedItems, totalHeight]);

  const runScroller = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const newScrollTop = target.scrollTop;
      const index =
        minIndex + Math.ceil((newScrollTop - toleranceHeight) / itemHeight);
      const newData = get(index, bufferedItems);
      const newTopPaddingHeight = Math.max((index - minIndex) * itemHeight, 0);
      const newBottomPaddingHeight = Math.max(
        totalHeight -
          newTopPaddingHeight -
          newData.length * itemHeight -
          itemHeight,
        0
      );
      topPaddingHeight.current = newTopPaddingHeight;
      bottomPaddingHeight.current = newBottomPaddingHeight;

      if (!lastIndex.current) {
        setData(newData);
      }
      if (lastIndex.current !== index) {
        setData(newData);
        lastIndex.current = index;
      }
    },
    [minIndex, toleranceHeight, itemHeight, get, bufferedItems, totalHeight]
  );

  return { topPaddingHeight, bottomPaddingHeight, data, runScroller };
};
